import { createReducer, on } from '@ngrx/store';

import { GlobalBookingTravelClass, TripType } from '@fcom/core';
import { LocalDate, setIn, updateIn } from '@fcom/utils';

import { GlobalBookingActions } from '../actions';
import { GlobalBookingState } from '../store.interface';
import { MAX_PAX_AMOUNT, MIN_LEAD_PAX_AMOUNT } from '../constants';

export const initialState: GlobalBookingState = {
  dates: {
    departureDate: undefined,
    returnDate: undefined,
  },
  tripType: TripType.RETURN,
  travelClasses: {
    selected: GlobalBookingTravelClass.ECONOMY,
    availability: {
      ECONOMY: true,
      BUSINESS: true,
      FIRST: false,
      ECOPREMIUM: false,
      MIXED: false,
    },
  },
  origin: undefined,
  routeType: undefined,
  paxAmount: {
    adults: 1,
    c15s: 0,
    children: 0,
    infants: 0,
  },
  locations: [{ origin: undefined, destination: undefined }],
};

const globalBooking = createReducer(
  initialState,
  on(GlobalBookingActions.setDates, (state, { dates }) => ({
    ...state,
    dates,
  })),
  on(GlobalBookingActions.setTripType, (state, { tripType }) => ({
    ...state,
    tripType,
    dates: tripType === TripType.ONEWAY ? { ...state.dates, returnDate: undefined } : state.dates,
    locations: tripType === TripType.MULTICITY ? [state.locations[0], {}] : [state.locations[0]],
  })),
  on(GlobalBookingActions.setTravelClass, (state, { travelClass }) => ({
    ...state,
    travelClasses: { ...state.travelClasses, selected: travelClass },
  })),
  on(GlobalBookingActions.setOrigin, (state, { origin }) => ({
    ...state,
    origin,
  })),
  on(GlobalBookingActions.setRouteType, (state, { routeType }) => ({
    ...state,
    routeType,
  })),
  on(GlobalBookingActions.setPaxAmount, (state, { paxAmount }) => ({
    ...state,
    paxAmount,
  })),
  on(GlobalBookingActions.increasePaxAmountField, (state, { field, increment }) =>
    updatePaxAmountField(state, field, (old) => old + increment)
  ),
  on(GlobalBookingActions.decreasePaxAmountField, (state, { field, decrement }) =>
    updatePaxAmountField(state, field, (old) => Math.max(old - decrement, 0))
  ),
  on(GlobalBookingActions.setDiscountCode, (state, { discountCode }) => ({
    ...state,
    discountCode,
  })),
  on(GlobalBookingActions.setSelection, (state, { selection }) => ({
    ...state,
    paxAmount: selection.paxAmount,
    locations: [
      {
        origin: selection?.origin,
        destination: selection?.destination,
      },
    ],
    tripType: selection.tripType,
    travelClasses: { ...state.travelClasses, selected: selection.travelClass },
    dates:
      selection.tripType === TripType.ONEWAY
        ? { departureDate: new LocalDate(selection.travelDates.departureDate), returnDate: undefined }
        : {
            departureDate: new LocalDate(selection.travelDates.departureDate),
            returnDate: new LocalDate(selection.travelDates.returnDate),
          },
    discountCode: selection.discountCode ?? undefined,
  })),
  on(GlobalBookingActions.resetSelection, (state) => ({
    ...state,
    paxAmount: initialState.paxAmount,
    locations: [
      {
        origin: state.locations[0].origin,
      },
    ],
    tripType: initialState.tripType,
    travelClasses: initialState.travelClasses,
    dates: initialState.dates,
    discountCode: initialState.discountCode,
  })),
  on(GlobalBookingActions.setLocations, (state, { locations }) => ({
    ...state,
    locations,
  })),
  on(GlobalBookingActions.updateLocationPair, (state, { locationPair, index }) => ({
    ...state,
    locations: state.locations.map((loc, i) => (i === index ? locationPair : loc)),
  }))
);

const updatePaxAmountField = (state: GlobalBookingState, field: string, mutator: (oldValue: number) => number) => {
  const newState = disallowMoreInfantsThanAdults(updateIn(state, ['paxAmount', field], mutator));
  const newLeadPaxAmount = newState?.paxAmount?.adults;
  const newTotal = newLeadPaxAmount + state?.paxAmount?.c15s + state?.paxAmount?.children;
  if (newTotal > MAX_PAX_AMOUNT || newLeadPaxAmount < MIN_LEAD_PAX_AMOUNT) {
    return state;
  }
  return newState;
};

const disallowMoreInfantsThanAdults = (state: GlobalBookingState) => {
  const numAdults = state.paxAmount.adults;
  if (state.paxAmount.infants > numAdults) {
    return setIn(state, 'paxAmount.infants', numAdults);
  }
  return state;
};

export const globalBookingReducer = (
  state: GlobalBookingState,
  action: GlobalBookingActions.GlobalBookingActionsUnion
): GlobalBookingState => {
  return globalBooking(state, action);
};
